import React, { useState, useEffect, useRef } from 'react';
import { ElternEintrag, Rolle, ROLLEN, elternApi } from './eltern-api';
import { toast } from '../../lib/toast';
import { confirm } from '../../lib/confirm';
import { LdSheet } from '../ui/LdSheet';

interface Props {
  open: boolean;
  schuelerId: number;
  onClose: () => void;
  onErstellt: () => void;
  // Für Edit-Modus:
  editDaten?: ElternEintrag;
}

const inp: React.CSSProperties = {
  width: '100%', padding: '10px 13px', borderRadius: 10,
  border: '1.5px solid var(--ld-outline-soft)', fontSize: 15, outline: 'none',
  boxSizing: 'border-box', background: 'var(--ld-bg-input)', color: 'var(--ld-text-primary)',
};
const lbl: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, color: 'var(--ld-text-secondary)',
  letterSpacing: .4, display: 'block', marginBottom: 5,
};

export function ElternErstellenModal({ open, schuelerId, onClose, onErstellt, editDaten }: Props) {
  const istEdit = !!editDaten;
  const [name,           setName]           = useState('');
  const [email,          setEmail]          = useState('');
  const [telefon,        setTelefon]        = useState('');
  const [notiz,          setNotiz]          = useState('');
  const [rolle,          setRolle]          = useState<Rolle>('mutter');
  const [hauptkontakt,   setHauptkontakt]   = useState(true);
  const [laden,          setLaden]          = useState(false);
  const [loeschenConfirm, setLoeschenConfirm] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      if (editDaten) {
        setName(editDaten.name ?? '');
        setEmail(editDaten.email ?? '');
        setTelefon(editDaten.telefon ?? '');
        setNotiz(editDaten.notiz ?? '');
        setRolle(editDaten.rolle);
        setHauptkontakt(editDaten.ist_hauptkontakt === 1);
      } else {
        setName(''); setEmail(''); setTelefon(''); setNotiz('');
        setRolle('mutter'); setHauptkontakt(true);
      }
      setLoeschenConfirm(false);
      setTimeout(() => emailRef.current?.focus(), 80);
    }
  }, [open, editDaten]);

  if (!open) return null;

  async function handleSpeichern() {
    if (!email.trim()) return;
    setLaden(true);
    try {
      if (istEdit && editDaten) {
        await Promise.all([
          elternApi.aktualisierenEltern(editDaten.id, { name, email, telefon, notiz }),
          elternApi.aktualisierenLink(editDaten.eltern_schueler_id, { rolle, ist_hauptkontakt: hauptkontakt }),
        ]);
        toast('Elternteil gespeichert', 'success');
      } else {
        await elternApi.hinzufuegen(schuelerId, { name, email, telefon, notiz, rolle, ist_hauptkontakt: hauptkontakt });
        toast('Elternteil hinzugefügt', 'success');
      }
      onErstellt();
      onClose();
    } catch (err: any) {
      toast(err.message ?? 'Fehler.', 'error');
    } finally {
      setLaden(false);
    }
  }

  async function handleLoeschen() {
    if (!editDaten) return;
    setLaden(true);
    try {
      await elternApi.loeschen(editDaten.eltern_schueler_id);
      toast('Verknüpfung gelöst', 'success');
      onErstellt();
      onClose();
    } catch (err: any) {
      toast(err.message ?? 'Fehler.', 'error');
    } finally {
      setLaden(false);
    }
  }

  // Welle U7: Elterndatensatz bei allen Kindern löschen (nicht nur diese Verknüpfung)
  async function handleElternLoeschen() {
    if (!editDaten) return;
    const name = editDaten.name?.trim() || editDaten.email || 'Dieser Elternkontakt';
    const ja = await confirm(
      `${name} wird bei allen verknüpften Kindern entfernt. Mails von dieser Adresse werden danach keinem Kind mehr zugeordnet.`,
      { title: 'Elternkontakt löschen?', confirmLabel: 'Löschen', danger: true });
    if (!ja) return;
    setLaden(true);
    try {
      await elternApi.loeschenEltern(editDaten.id);
      toast('Elternkontakt gelöscht', 'success');
      onErstellt();
      onClose();
    } catch (err: any) {
      toast(err.message ?? 'Fehler.', 'error');
    } finally {
      setLaden(false);
    }
  }

  // Portal: das Profil liegt in einem eigenen Stacking-Kontext, sonst verdeckt die Bottom-Nav die Knöpfe unten
  return (
    <LdSheet open onClose={onClose} maxWidth={520} kopf={{ titel: istEdit ? 'Elternteil bearbeiten' : 'Elternteil hinzufügen' }}>
      <div>

        <div style={{ marginBottom: 12 }}>
          <label style={lbl}>Name (optional)</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Müller, Andrea" style={inp} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={lbl}>E-Mail *</label>
          <input ref={emailRef} value={email} onChange={e => setEmail(e.target.value)}
            type="email" placeholder="andrea.mueller@gmx.de" style={inp} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={lbl}>Telefon (optional)</label>
          <input value={telefon} onChange={e => setTelefon(e.target.value)} placeholder="+49 177 …" style={inp} />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={lbl}>Notiz (optional)</label>
          <textarea value={notiz} onChange={e => setNotiz(e.target.value)}
            rows={2} placeholder="z.B. Bevorzugt Anrufe abends …"
            style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} />
        </div>

        {/* Rollen-Auswahl */}
        <div style={{ marginBottom: 14 }}>
          <label style={lbl}>Rolle</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {ROLLEN.map(r => (
              <button key={r.value} onClick={() => setRolle(r.value)} style={{
                padding: '8px 14px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                border: rolle === r.value ? '2px solid var(--ld-primary)' : '1.5px solid var(--ld-outline-soft)',
                background: rolle === r.value ? 'var(--ld-primary-light, #EDE9F8)' : 'var(--ld-bg-input)',
                color: rolle === r.value ? 'var(--ld-primary)' : 'var(--ld-text-secondary)',
              }}>
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {/* Hauptkontakt */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, cursor: 'pointer' }}>
          <input type="checkbox" checked={hauptkontakt} onChange={e => setHauptkontakt(e.target.checked)}
            style={{ width: 18, height: 18, accentColor: 'var(--ld-primary)' }} />
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--ld-text-primary)' }}>Hauptkontakt</span>
          <span style={{ fontSize: 12, color: 'var(--ld-text-secondary)' }}>(wird in Mail-Compose vorgeschlagen)</span>
        </label>

        {/* Löschen (nur Edit) */}
        {istEdit && !loeschenConfirm && (
          <button onClick={() => setLoeschenConfirm(true)} disabled={laden} style={{ width: '100%', padding: '10px 0', borderRadius: 12, border: '1.5px solid #FECACA', background: '#FEF2F2', color: '#DC2626', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginBottom: 10 }}>
            Verknüpfung lösen
          </button>
        )}
        {istEdit && loeschenConfirm && (
          <div style={{ background: '#FEF2F2', borderRadius: 12, padding: '12px 14px', marginBottom: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ flex: 1, fontSize: 13, color: '#DC2626', fontWeight: 600 }}>Verknüpfung wirklich lösen?</span>
            <button onClick={() => setLoeschenConfirm(false)} style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: 'white', fontSize: 13, cursor: 'pointer' }}>Nein</button>
            <button onClick={handleLoeschen} disabled={laden} style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: '#DC2626', color: 'white', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Lösen</button>
          </div>
        )}
        {istEdit && (
          <button onClick={handleElternLoeschen} disabled={laden} style={{ width: '100%', padding: '10px 0', borderRadius: 12, border: '1.5px solid #FECACA', background: 'white', color: '#DC2626', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginBottom: 10 }}>
            Elternkontakt löschen
          </button>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '12px 0', borderRadius: 12, border: '1.5px solid var(--ld-outline-soft)', background: 'var(--ld-bg-input)', fontSize: 15, fontWeight: 600, cursor: 'pointer', color: 'var(--ld-text-secondary)' }}>Abbrechen</button>
          <button onClick={handleSpeichern} disabled={!email.trim() || laden} style={{ flex: 2, padding: '12px 0', borderRadius: 12, border: 'none', background: !email.trim() || laden ? '#D0CAEC' : 'var(--ld-primary)', color: 'white', fontSize: 15, fontWeight: 700, cursor: !email.trim() || laden ? 'default' : 'pointer' }}>
            {laden ? '…' : istEdit ? 'Speichern' : 'Hinzufügen'}
          </button>
        </div>
      </div>
    </LdSheet>
  );
}
